import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";

const CONSENT_KEY = "ch_analytics_consent";

const applyConsent = (value) => {
  const ph = window.posthog;
  if (!ph) return;
  try {
    if (value === "accepted") {
      ph.opt_in_capturing();
    } else {
      ph.opt_out_capturing();
    }
  } catch (err) {
    console.warn("Analytics unavailable", err);
  }
};

export const CookieBanner = () => {
  const [consent, setConsent] = useState(() => localStorage.getItem(CONSENT_KEY));
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    applyConsent(consent);
    if (!consent) {
      const id = setTimeout(() => setVisible(true), 1200);
      return () => clearTimeout(id);
    }
  }, [consent]);

  useEffect(() => {
    const open = () => setVisible(true);
    window.addEventListener("ch:open-cookie-banner", open);
    return () => window.removeEventListener("ch:open-cookie-banner", open);
  }, []);

  const choose = (value) => {
    localStorage.setItem(CONSENT_KEY, value);
    applyConsent(value);
    try {
      if (value === "accepted") window.posthog?.capture?.("cookie_accepted");
    } catch (err) {
      console.warn("Analytics unavailable", err);
    }
    setConsent(value);
    setVisible(false);
  };

  return (
    <AnimatePresence>
      {visible && (
        <motion.div
          initial={{ y: 80, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          exit={{ y: 80, opacity: 0 }}
          transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
          className="fixed inset-x-0 bottom-0 z-[70] border-t border-[#C4A36A]/40 bg-[#F7F3EA] text-[#1A120C]"
          data-testid="cookie-banner"
        >
          <div className="mx-auto flex max-w-7xl flex-col items-center gap-4 px-6 py-4 sm:flex-row sm:justify-between md:px-12">
            <p className="text-center text-xs leading-relaxed text-[#3A2C20] sm:max-w-xl sm:text-left" data-testid="cookie-banner-text">
              We use privacy-respecting analytics to see which sections people read — no advertising profiles, ever.{" "}
              <Link
                to="/privacy"
                className="text-[#A08448] underline underline-offset-2 transition-colors duration-300 hover:text-[#7A5A14]"
                data-testid="cookie-learn-more-link"
              >
                Learn more
              </Link>
            </p>
            <div className="flex items-center gap-3">
              <button
                onClick={() => choose("declined")}
                data-testid="cookie-decline-button"
                className="rounded-full border border-[#C4A36A] px-5 py-2 text-xs font-bold uppercase tracking-[0.15em] text-[#A08448] transition-colors duration-300 hover:bg-[#C4A36A]/10 hover:text-[#7A5A14]"
              >
                Decline
              </button>
              <button
                onClick={() => choose("accepted")}
                data-testid="cookie-accept-button"
                className="rounded-full bg-[#C0965A] px-5 py-2 text-xs font-bold uppercase tracking-[0.15em] text-[#FFF8E8] transition-colors duration-200 hover:bg-[#B0894C] active:scale-95"
              >
                Accept analytics
              </button>
            </div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};
